import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Typography, Grid, Paper, Box, CircularProgress, Alert, Button, IconButton, Tooltip, Card, CardContent, Badge, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, ListItemIcon, Divider } from '@mui/material';
import { Receipt, Assessment, Add as AddIcon, StickyNote2 as NoteIcon, Delete as DeleteIcon, CalendarToday as CalendarIcon, ArrowBackIos, ArrowForwardIos, Build, PointOfSale, Payment, Edit as EditIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { SECTIONS, hasSectionAccess, canManageCashRegister, hasPermission } from '../utils/roles';
import ReportDialog from '../components/ReportDialog';
import CashRegisterDialog from '../components/CashRegisterDialog';
import CashRegisterMenuDialog from '../components/CashRegisterMenuDialog';
import CashRegisterTransferDialog from '../components/CashRegisterTransferDialog';
import CashRegisterHistoryDialog from '../components/CashRegisterHistoryDialog';
import NewPaymentDialog from '../components/NewPaymentDialog';
import EditTodayPaymentsDialog from '../components/EditTodayPaymentsDialog';
import MaintenanceForm from '../components/maintenance/MaintenanceForm';
import NoteDialog from '../components/NoteDialog';
import logger from '../utils/logger';

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [cashRegisterDialogOpen, setCashRegisterDialogOpen] = useState(false);
  const [cashRegisterMenuOpen, setCashRegisterMenuOpen] = useState(false);
  const [cashRegisterTransferOpen, setCashRegisterTransferOpen] = useState(false);
  const [cashRegisterTransferType, setCashRegisterTransferType] = useState('PRELIEVO');
  const [cashRegisterHistoryOpen, setCashRegisterHistoryOpen] = useState(false);
  const [cashRegisterStatus, setCashRegisterStatus] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarNotes, setCalendarNotes] = useState([]);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const [calendarDialogMode, setCalendarDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [todayNotesDialog, setTodayNotesDialog] = useState(false);
  const [todayNotes, setTodayNotes] = useState([]);
  const [notesPage, setNotesPage] = useState(0);
  const notesPerPage = 9;
  const [newPaymentOpen, setNewPaymentOpen] = useState(false);
  const [editPaymentsOpen, setEditPaymentsOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const { user } = useAuth();

  const summaryData = useMemo(() => {
    if (!user) return [];

    const items = [];

    if (hasSectionAccess(user, SECTIONS.SALES)) {
      items.push({
        title: 'Nuova Vendita',
        count: <AddIcon fontSize="large" />,
        icon: <Receipt fontSize="large" color="primary" />,
        path: '/dashboard/sales',
        permission: 'ACCESS_SALES'
      });
    }

    if (hasSectionAccess(user, SECTIONS.MAINTENANCE)) {
      items.push({
        title: 'Nuova Manutenzione',
        count: <AddIcon fontSize="large" />,
        icon: <Build fontSize="large" color="primary" />,
        path: '#',
        permission: 'ACCESS_MAINTENANCE',
        isAction: true,
        actionType: 'newMaintenance'
      });
    }

    if (hasPermission(user?.role, 'ACCESS_SETTINGS')) {
      items.push({
        title: 'Genera Report',
        count: <AddIcon fontSize="large" />,
        icon: <Assessment fontSize="large" color="primary" />,
        path: '#',
        isAction: true,
        actionType: 'report'
      });
    }

    items.push({
      title: 'Nuovo Pagamento',
      count: <AddIcon fontSize="large" />,
      icon: <Payment fontSize="large" color="error" />,
      path: '#',
      isAction: true,
      actionType: 'newPayment'
    });

    items.push({
      title: 'Modifica Pagamento',
      count: <EditIcon fontSize="large" />,
      icon: <EditIcon fontSize="large" color="primary" />,
      path: '#',
      isAction: true,
      actionType: 'editPayments'
    });

    const isCashOpen = !!cashRegisterStatus?.openSession;
    items.push({
      title: isCashOpen ? 'Chiusura Cassa' : 'Apertura Cassa',
      count: <PointOfSale fontSize="large" />,
      icon: <PointOfSale fontSize="large" color={isCashOpen ? 'warning' : 'success'} />,
      path: '#',
      permission: 'ACCESS_REPORTS',
      isAction: true,
      actionType: 'cashRegister'
    });

    return items;
  }, [user, cashRegisterStatus?.openSession]);

  const handleCashRegisterClick = () => {
    if (canManageCashRegister(user)) {
      setCashRegisterMenuOpen(true);
    } else {
      setCashRegisterDialogOpen(true);
    }
  };

  const handleCashRegisterUpdated = async () => {
    await fetchCashRegisterStatus();
  };

  const isCashOpen = !!cashRegisterStatus?.openSession;

  const fetchCashRegisterStatus = useCallback(async () => {
    if (!user) return null;
    try {
      const res = await api.get('/cash-register/status');
      setCashRegisterStatus(res.data);
      return res.data;
    } catch (err) {
      logger.error('Errore stato cassa:', err);
      return null;
    }
  }, [user]);

  const fetchNotes = useCallback(async () => {
    try {
      setNotesLoading(true);
      const response = await api.get('/notes');
      setNotes(response.data);
    } catch (error) {
      logger.error('Error fetching notes:', error);
    } finally {
      setNotesLoading(false);
    }
  }, []);

  const handleAddNote = () => {
    setNoteToEdit(null);
    setNoteDialogOpen(true);
  };

  const handleEditNote = (note) => {
    setNoteToEdit(note);
    setNoteDialogOpen(true);
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm('Sei sicuro di voler eliminare questa nota?')) {
      try {
        await api.delete(`/notes/${noteId}`);
        setNotes(notes.filter(note => note.id !== noteId));
      } catch (error) {
        logger.error('Error deleting note:', error);
        alert('Errore nell\'eliminazione della nota');
      }
    }
  };

  const handleNoteSaved = async (savedNote) => {
    // Ricarica tutte le note dal server per assicurarsi che calendario e lista siano aggiornati
    await fetchNotes();
    // Reset alla prima pagina per mostrare la nuova nota (se ordinamento per data recente)
    setNotesPage(0);
  };

  const handleNoteDialogClose = () => {
    setNoteDialogOpen(false);
    setNoteToEdit(null);
    setCalendarNotes([]);
    setCurrentNoteIndex(0);
    setCalendarDialogMode('add');
    setSelectedCalendarDate(null);
  };

  const handleCalendarDayClick = (day) => {
    if (!day) return;
    
    const clickedDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    const dayNotes = getNotesForDate(day);
    
    setSelectedCalendarDate(clickedDate);
    
    if (dayNotes.length > 0) {
      // Ha note - modalità modifica
      setCalendarNotes(dayNotes);
      setCurrentNoteIndex(0);
      setNoteToEdit(dayNotes[0]);
      setCalendarDialogMode('edit');
    } else {
      // Nessuna nota - modalità aggiungi
      setCalendarNotes([]);
      setCurrentNoteIndex(0);
      setNoteToEdit({
        title: '',
        content: '',
        date: formatLocalDate(clickedDate)
      });
      setCalendarDialogMode('add');
    }
    
    setNoteDialogOpen(true);
  };

  const navigateCalendarNotes = (direction) => {
    if (calendarNotes.length === 0) return;
    
    let newIndex = currentNoteIndex + direction;
    if (newIndex < 0) newIndex = calendarNotes.length - 1;
    if (newIndex >= calendarNotes.length) newIndex = 0;
    
    setCurrentNoteIndex(newIndex);
    setNoteToEdit(calendarNotes[newIndex]);
  };
  
  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getNotesForDate = (day) => {
    if (!day) return [];
    const targetDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    const targetDateString = formatLocalDate(targetDate);
    
    return notes.filter(note => {
      const noteDate = new Date(note.date);
      const noteDateString = formatLocalDate(noteDate);
      return noteDateString === targetDateString;
    });
  };

  useEffect(() => {
    if (notes.length === 0) return;

    const todayString = formatLocalDate(new Date());
    const todayNotesList = notes.filter((note) => {
      const noteDateString = formatLocalDate(new Date(note.date));
      return noteDateString === todayString;
    });

    if (todayNotesList.length > 0) {
      setTodayNotes(todayNotesList);
      setTodayNotesDialog(true);
    }
  }, [notes]);

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedDate(newDate);
  };

  // Notes pagination functions
  const getDisplayedNotes = () => {
    const sortedNotes = notes.sort((a, b) => new Date(b.date) - new Date(a.date));
    const startIndex = notesPage * notesPerPage;
    return sortedNotes.slice(startIndex, startIndex + notesPerPage);
  };

  const getTotalPages = () => {
    return Math.ceil(notes.length / notesPerPage);
  };

  const handlePreviousPage = () => {
    setNotesPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setNotesPage(prev => Math.min(getTotalPages() - 1, prev + 1));
  };

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

  const handleCardAction = (actionType) => {
    switch (actionType) {
      case 'cashRegister':
        handleCashRegisterClick();
        break;
      case 'report':
        setReportDialogOpen(true);
        break;
      case 'newPayment':
        setNewPaymentOpen(true);
        break;
      case 'editPayments':
        setEditPaymentsOpen(true);
        break;
      case 'newMaintenance':
        setMaintenanceDialogOpen(true);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!user) return;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        await fetchCashRegisterStatus();
      } catch (error) {
        logger.error('❌ Errore nel recupero dei dati dashboard:', error);
        setError(error.response?.data?.message || 'Errore nel caricamento dei dati della dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    fetchNotes();
  }, [user, fetchCashRegisterStatus, fetchNotes]);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: 3, backgroundColor: '#f5f5f5' }}>
      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Statistics Cards - Responsive */}
      <Grid container spacing={2} sx={{ width: '100%', mb: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          summaryData.map((item, index) => (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={4} 
              lg={3}
              key={index}
              sx={{
                // Per schermi molto grandi (xl), permette 5 card per riga usando flex
                '@media (min-width: 1536px)': {
                  flexBasis: 'calc(20% - 16px)',
                  maxWidth: 'calc(20% - 16px)',
                  flexGrow: 0
                },
                // Per schermi grandi (lg), 4 card per riga
                '@media (min-width: 1200px) and (max-width: 1535px)': {
                  flexBasis: 'calc(25% - 16px)',
                  maxWidth: 'calc(25% - 16px)',
                  flexGrow: 0
                }
              }}
            >
              <Paper
                component={item.isAction ? 'div' : Link}
                to={item.isAction ? undefined : item.path}
                onClick={item.isAction ? () => handleCardAction(item.actionType) : undefined}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: 110,
                  justifyContent: 'center',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                  }
                }}
                elevation={3}
              >
                {item.icon}
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ fontSize: '1.8rem' }}>
                    {item.count}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, textAlign: 'center', fontWeight: 'medium', fontSize: '0.875rem' }}>
                  {item.title}
                </Typography>
              </Paper>
            </Grid>
          ))
        )}
      </Grid>

      {/* Main Content - Calendar Left, Notes Right */}
      <Grid container spacing={3} sx={{ width: '100%' }}>
        {/* Calendar Section - Left Side */}
        <Grid item xs={12} md={6}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                Calendario Note
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddNote}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Aggiungi Nota
              </Button>
            </Box>
            
            <Card sx={{ width: '100%' }}>
              <CardContent>
                {/* Calendar Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Button 
                    onClick={() => navigateMonth(-1)}
                    variant="outlined"
                    size="small"
                  >
                    ‹
                  </Button>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon />
                    {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
                  </Typography>
                  <Button 
                    onClick={() => navigateMonth(1)}
                    variant="outlined"
                    size="small"
                  >
                    ›
                  </Button>
                </Box>

                {/* Calendar Grid */}
                <Grid container spacing={1}>
                  {/* Day headers */}
                  {dayNames.map((day) => (
                    <Grid item xs={12/7} key={day}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        py: 1, 
                        fontWeight: 'bold',
                        color: 'text.secondary',
                        fontSize: '0.875rem'
                      }}>
                        {day}
                      </Box>
                    </Grid>
                  ))}

                  {/* Calendar days */}
                  {getDaysInMonth(selectedDate).map((day, index) => {
                    const dayNotes = getNotesForDate(day);
                    const isToday = day && 
                      new Date().getDate() === day && 
                      new Date().getMonth() === selectedDate.getMonth() && 
                      new Date().getFullYear() === selectedDate.getFullYear();

                    return (
                      <Grid item xs={12/7} key={index}>
                        <Box
                          sx={{
                            minHeight: 60,
                            p: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: day ? 
                              (isToday ? 'primary.light' : 'background.paper') : 
                              'grey.50',
                            position: 'relative',
                            cursor: day ? 'pointer' : 'default',
                            '&:hover': day ? {
                              backgroundColor: isToday ? 'primary.main' : 'action.hover'
                            } : {}
                          }}
                          onClick={() => handleCalendarDayClick(day)}
                        >
                          {day && (
                            <>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: isToday ? 'bold' : 'normal',
                                  color: isToday ? 'white' : 'text.primary'
                                }}
                              >
                                {day}
                              </Typography>
                              {dayNotes.length > 0 && (
                                <Badge
                                  badgeContent={dayNotes.length}
                                  color="secondary"
                                  sx={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    '& .MuiBadge-badge': {
                                      fontSize: '0.625rem',
                                      minWidth: 16,
                                      height: 16
                                    }
                                  }}
                                />
                              )}
                              {dayNotes.length > 0 && (
                                <Box sx={{ mt: 0.5 }}>
                                  {dayNotes.slice(0, 1).map((note, noteIndex) => (
                                    <Typography
                                      key={noteIndex}
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        fontSize: '0.6rem',
                                        color: isToday ? 'white' : 'text.secondary',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.2
                                      }}
                                    >
                                      {note.title}
                                    </Typography>
                                  ))}
                                  {dayNotes.length > 1 && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.6rem',
                                        color: isToday ? 'white' : 'text.secondary',
                                        fontStyle: 'italic'
                                      }}
                                    >
                                      +{dayNotes.length - 1} altre
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Notes Section - Right Side */}
        <Grid item xs={12} md={6}>
          <Box sx={{ width: '100%' }}>
            {/* Header con controlli di paginazione */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">
                Note Recenti
              </Typography>
              
              {notes.length > notesPerPage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    onClick={handlePreviousPage}
                    disabled={notesPage === 0}
                    size="small"
                  >
                    <ArrowBackIos fontSize="small" />
                  </IconButton>
                  
                  <Typography variant="body2" color="text.secondary">
                    {notesPage + 1} / {getTotalPages()}
                  </Typography>
                  
                  <IconButton
                    onClick={handleNextPage}
                    disabled={notesPage >= getTotalPages() - 1}
                    size="small"
                  >
                    <ArrowForwardIos fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            
            <Grid container spacing={2}>
              {/* Existing Notes */}
              {notesLoading ? (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                </Grid>
              ) : (
                getDisplayedNotes().map((note) => (
                  <Grid item xs={12} sm={6} md={4} key={note.id}>
                    <Paper
                      onClick={() => handleEditNote(note)}
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 140,
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                        }
                      }}
                      elevation={2}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <NoteIcon sx={{ fontSize: 20, color: 'primary.main', mr: 1 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 'bold',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1
                          }}
                        >
                          {note.title}
                        </Typography>
                        <Tooltip title="Elimina nota">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNote(note.id);
                            }}
                            sx={{
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              '.MuiPaper-root:hover &': {
                                opacity: 1
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {new Date(note.date).toLocaleDateString('it-IT')}
                      </Typography>
                      
                      {note.content && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            flex: 1
                          }}
                        >
                          {note.content}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))
              )}
            </Grid>
            
            {/* Info paginazione */}
            {notes.length > 0 && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Mostrando {getDisplayedNotes().length} di {notes.length} note totali
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Welcome Text - Commented out as requested */}
      {/* 
      <Box sx={{ mt: 4, width: '100%', textAlign: 'left' }}>
        <Typography variant="h5" gutterBottom>
          Benvenuto nel Gestionale Mobility Hub
        </Typography>
        <Typography variant="body1" paragraph>
          Questo è il pannello di controllo principale del tuo software gestionale per le manutenzioni e le vendite.
          Da qui puoi accedere a tutte le funzionalità del sistema.
        </Typography>
        <Typography variant="body1">
          Utilizza il menu in alto per navigare tra le diverse sezioni dell'applicazione.
        </Typography>
      </Box>
      */}

      {/* Report Dialog */}
      <ReportDialog 
        open={reportDialogOpen} 
        onClose={() => setReportDialogOpen(false)} 
      />

      <CashRegisterMenuDialog
        open={cashRegisterMenuOpen}
        onClose={() => setCashRegisterMenuOpen(false)}
        isCashOpen={isCashOpen}
        onOpenClose={() => {
          setCashRegisterMenuOpen(false);
          setCashRegisterDialogOpen(true);
        }}
        onTransfer={(type) => {
          setCashRegisterMenuOpen(false);
          setCashRegisterTransferType(type);
          setCashRegisterTransferOpen(true);
        }}
        onHistory={() => {
          setCashRegisterMenuOpen(false);
          setCashRegisterHistoryOpen(true);
        }}
      />

      <CashRegisterTransferDialog
        open={cashRegisterTransferOpen}
        onClose={() => setCashRegisterTransferOpen(false)}
        type={cashRegisterTransferType}
        onSaved={handleCashRegisterUpdated}
      />

      <CashRegisterHistoryDialog
        open={cashRegisterHistoryOpen}
        onClose={() => setCashRegisterHistoryOpen(false)}
      />

      <CashRegisterDialog
        open={cashRegisterDialogOpen}
        onClose={() => setCashRegisterDialogOpen(false)}
        onUpdated={handleCashRegisterUpdated}
      />

      <NewPaymentDialog
        open={newPaymentOpen}
        onClose={() => setNewPaymentOpen(false)}
      />

      <EditTodayPaymentsDialog
        open={editPaymentsOpen}
        onClose={() => setEditPaymentsOpen(false)}
      />

      <MaintenanceForm
        open={maintenanceDialogOpen}
        onClose={() => setMaintenanceDialogOpen(false)}
        onSave={async (data) => {
          try {
            const response = await api.post('/maintenance', data);
            setMaintenanceDialogOpen(false);
            return response.data;
          } catch (err) {
            alert(
              err.response?.data?.msg ||
              err.message ||
              'Errore durante il salvataggio della manutenzione.'
            );
            throw err;
          }
        }}
      />

      {/* Note Dialog */}
      <NoteDialog
        open={noteDialogOpen}
        handleClose={handleNoteDialogClose}
        onSave={handleNoteSaved}
        noteToEdit={noteToEdit}
        showNavigation={calendarDialogMode === 'edit' && calendarNotes.length > 1}
        currentIndex={currentNoteIndex}
        totalNotes={calendarNotes.length}
        onNavigate={navigateCalendarNotes}
        selectedDate={selectedCalendarDate}
      />

      {/* Today's Notes Notification Dialog */}
      <Dialog
        open={todayNotesDialog}
        onClose={() => setTodayNotesDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NoteIcon color="primary" />
            <Typography variant="h6" sx={{ color: '#000000' }}>
              Note di Oggi ({new Date().toLocaleDateString('it-IT')})
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Hai {todayNotes.length} {todayNotes.length === 1 ? 'nota' : 'note'} per oggi:
          </Typography>
          <List>
            {todayNotes.map((note, index) => (
              <React.Fragment key={note.id}>
                <ListItem 
                  button 
                  onClick={() => {
                    setTodayNotesDialog(false);
                    handleEditNote(note);
                  }}
                  sx={{ 
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon>
                    <NoteIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={note.title}
                    secondary={note.content ? note.content.substring(0, 100) + (note.content.length > 100 ? '...' : '') : 'Nessun contenuto'}
                    primaryTypographyProps={{ fontWeight: 'medium', sx: { color: '#000000 !important' } }}
                  />
                </ListItem>
                {index < todayNotes.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTodayNotesDialog(false)} color="primary">
            Chiudi
          </Button>
          <Button 
            onClick={() => {
              setTodayNotesDialog(false);
              handleAddNote();
            }} 
            variant="contained" 
            color="primary"
            startIcon={<AddIcon />}
          >
            Aggiungi Nota
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;